import React from 'react';
import axios from 'axios';
import gql from 'graphql-tag';
import { graphql } from 'react-apollo';
import { isEmpty } from 'lodash';
import { generatedHeadlineForUser, generatedBioForUser } from '../../lib/userUtils';
import AIHeadline from '../AiHeadline/AiHeadline';
import { S3Image, getAvatarStyle } from '../../lib/s3';
import PropTypes from 'prop-types';

// Import OnboardingContext
import { useOnboardingState } from '../../contexts/OnboardingContext';

// GraphQL mutation for updating user data
const UPDATE_CURRENT_USER = gql`
  mutation updateCurrentUser($headline: String, $bio: String, $ask_me_about: String, $profile_picture: String, $locale: String) {
    updateCurrentUser(headline: $headline, bio: $bio, ask_me_about: $ask_me_about, profile_picture: $profile_picture, locale: $locale)
  }
`;

// Create a higher-order component to connect the class component to context
const withOnboardingContext = WrappedComponent => {
  return props => {
    const context = useOnboardingState() || {};
    const currentLanguage = context.currentLanguage || props.currentLanguage || 'en';
    console.log('HeadlineAndAboutMeStep language from context:', currentLanguage);
    return <WrappedComponent {...props} currentLanguage={currentLanguage} onboardingContext={context} />;
  };
};

class HeadlineAndAboutMeStep extends React.Component {
  constructor(props) {
    super(props);
    
    // Get initial values from props or window objects
    const resumeData = window?.__WIZARD_DATA__?.resumeData || {};
    const user = props.user || {};
    const companySettings = props.companySettings || 
                           window?.__WIZARD_DATA__?.companySettings || 
                           window?.__INITIAL_WIZARD_DATA__?.companySettings || 
                           {};
    
    this.state = {
      // Form data
      headline: user.headline || resumeData.headline || '',
      bio: user.bio || resumeData.bio || '',
      askMeAbout: user.ask_me_about || resumeData.ask_me_about || '',
      currentAvatar: '', // Always start with no avatar selected in onboarding
      
      // Loading and error states
      loading: false,
      error: null,
      avatarError: null,
      headlineError: null,
      bioError: null,
      aiGenerating: false, // Track AI headline generation
      
      // Form validity
      formValid: false,
      
      // Track user interaction
      headlineUserEntered: !!(user.headline || resumeData.headline),
      avatarUserSelected: false // Always require explicit selection in onboarding
    };
    
    // Bind methods
    this.generateHeadline = this.generateHeadline.bind(this);
    this.handleTextChange = this.handleTextChange.bind(this);
    this.handleAvatarChange = this.handleAvatarChange.bind(this);
    this.saveHeadlineAndBio = this.saveHeadlineAndBio.bind(this);
    this.handleContinue = this.handleContinue.bind(this);
    this.handleBack = this.handleBack.bind(this);
    this.checkFormValidity = this.checkFormValidity.bind(this);
    this.getTranslatedText = this.getTranslatedText.bind(this);
    this.handleAiGenerationStart = this.handleAiGenerationStart.bind(this);
    this.handleAiGenerationEnd = this.handleAiGenerationEnd.bind(this);
    this.getCompanySettings = this.getCompanySettings.bind(this);
  }
  
  // Helper to get company settings from props or window objects
  getCompanySettings() {
    return this.props.companySettings || 
           window?.__WIZARD_DATA__?.companySettings || 
           window?.__INITIAL_WIZARD_DATA__?.companySettings || 
           {};
  }
  
  componentDidMount() {
    // Generate headline if needed
    setTimeout(this.generateHeadline, 200);
    
    // Check form validity
    this.checkFormValidity();
  }
  
  componentDidUpdate(prevProps, prevState) {
    // Check if user data changed
    if (this.props.user !== prevProps.user) {
      this.updateFromUserData(this.props.user);
    }
    
    // Check if form data changed
    if (
      prevState.headline !== this.state.headline ||
      prevState.bio !== this.state.bio ||
      prevState.askMeAbout !== this.state.askMeAbout ||
      prevState.currentAvatar !== this.state.currentAvatar
    ) {
      this.checkFormValidity();
    }

    // Update context form validity when local formValid state changes
    if (prevState.formValid !== this.state.formValid) {
      const { onboardingContext } = this.props;
      if (onboardingContext && typeof onboardingContext.setStepValid === 'function') {
        onboardingContext.setStepValid(this.state.formValid);
      }
    }
  }
  
  updateFromUserData(userData) {
    if (!userData) return;
    
    const updates = {};
    let hasChanges = false;
    
    // Check for updates to headline
    if (userData.headline && userData.headline !== this.state.headline) {
      updates.headline = userData.headline;
      updates.headlineUserEntered = true;  // Mark as user-entered when coming from user data
      hasChanges = true;
    }
    
    // Check for updates to bio
    if (userData.bio && userData.bio !== this.state.bio) {
      updates.bio = userData.bio;
      hasChanges = true;
    }
    
    // Check for updates to ask_me_about
    if (userData.ask_me_about && userData.ask_me_about !== this.state.askMeAbout) {
      updates.askMeAbout = userData.ask_me_about;
      hasChanges = true;
    }
    
    // In onboarding, we don't automatically load existing avatars
    // Users must explicitly select an avatar regardless of existing data
    
    // Update state if needed
    if (hasChanges) {
      this.setState(updates, () => {
        // Re-check form validity after state update
        this.checkFormValidity();
      });
    }
  }
  
  generateHeadline() {
    const { user, currentLanguage } = this.props;
    const companySettings = this.getCompanySettings();
    
    // If we already have a headline, just use that
    if (this.state.headline) {
      return;
    }
    
    // Ensure we have a valid user object with required properties
    if (!user) {
      console.error("No user data provided for headline generation");
      return;
    }
    
    // Ensure transition_goals exists to prevent errors
    if (!Array.isArray(user.transition_goals) || user.transition_goals.length === 0) {
      // Create a minimal user object with reasonable defaults for headline generation
      user.transition_goals = ["succession"];
    }
    
    // Constants from UserBio.jsx
    const companyHasCounty = companySettings.has_county || false;
    const countryForCompany = companySettings.country || 'ca';
    const agentWord = companySettings.agent_word || '';

    // Placeholder intl that routes new-style template ids to the component's
    // own translation table (no IntlProvider needed at this depth of the tree).
    // Expected id shape: `sign_up_pages.headline.<bucket>.<variant>` where
    // bucket ∈ growth|succession|exploring and variant ∈ one..five.
    const interpolate = (template, values) => {
      if (!template) return '';
      return Object.keys(values).reduce(
        (acc, k) => acc.split(`{${k}}`).join(values[k] == null ? '' : String(values[k])),
        template,
      );
    };
    const placeholderIntl = {
      formatMessage: ({ id }, values = {}) => {
        const match = id && id.match(/^sign_up_pages\.headline\.([a-z_]+)\.([a-z_]+)$/);
        if (match) {
          const [, bucket, variant] = match;
          const key = `headline.${bucket}.${variant}`;
          return interpolate(this.getTranslatedText(key), values);
        }
        if (id && id.startsWith('sign_up_pages.')) return id.replace('sign_up_pages.', '');
        return id;
      },
    };

    // Generate the headline — single code path for all tenants.
    let generatedHeadline = '';
    try {
      generatedHeadline = generatedHeadlineForUser(user, placeholderIntl, companyHasCounty, countryForCompany, agentWord);
      if (!generatedHeadline) {
        const template = this.getTranslatedText('headline.template.fallback');
        generatedHeadline = interpolate(template, {
          agent_word: agentWord || 'Financial Advisor',
          location: user.city || user.province || 'clients',
        });
      }
    } catch (error) {
      console.error('Error generating headline:', error);
      const template = this.getTranslatedText('headline.template.fallback');
      generatedHeadline = interpolate(template, {
        agent_word: agentWord || 'Financial Advisor',
        location: user.city || user.province || 'clients',
      });
    }

    // Generate bio too
    const generatedBio = generatedBioForUser(user);
    
    // Update state with generated headline and bio (but don't mark as user-entered)
    this.setState({
      headline: generatedHeadline,
      bio: generatedBio
      // Note: we intentionally do NOT set headlineUserEntered = true here
      // so that auto-generated headlines don't make the form valid
    }, () => {
      // Update the user data
      if (typeof this.props.onUserUpdate === 'function') {
        this.props.onUserUpdate({
          headline: generatedHeadline,
          bio: generatedBio
        });
      }
      
      // Check form validity
      this.checkFormValidity();
    });
  }
  
  handleTextChange(field, value) {
    const updates = { [field]: value };
    
    // Mark headline as user-entered when they type in it
    if (field === 'headline') {
      updates.headlineUserEntered = true;
    }
    
    this.setState(updates, () => {
      this.checkFormValidity();
    });
  }
  
  handleAvatarChange(imageName) {
    this.setState({ 
      currentAvatar: imageName,
      avatarError: null,
      avatarUserSelected: true  // Mark avatar as user-selected
    }, () => {
      this.checkFormValidity();
    });
  }
  
  handleAiGenerationStart() {
    this.setState({ aiGenerating: true });
  }
  
  handleAiGenerationEnd() {
    this.setState({ aiGenerating: false });
  }
  
  checkFormValidity() {
    const { headline, bio, askMeAbout, currentAvatar, headlineUserEntered, avatarUserSelected } = this.state;
    const companySettings = this.getCompanySettings();
    const askMeAboutMandatory = companySettings.ask_me_about_mandatory || false;
    
    // Check if user has manually entered headline and selected avatar
    const trimmedHeadline = (headline || '').trim();
    const headlineValid = trimmedHeadline.length > 0 && headlineUserEntered;
    const avatarValid = currentAvatar !== '' && avatarUserSelected;
    
    // Valid if user has manually entered headline and selected avatar
    let isValid = headlineValid && avatarValid;
    
    if (askMeAboutMandatory) {
      const trimmedAskMeAbout = (askMeAbout || '').trim();
      isValid = isValid && trimmedAskMeAbout.length > 0;
    }
    
    // Debug logging
    console.log('Form validation check:', {
      headline: trimmedHeadline,
      headlineUserEntered,
      headlineValid,
      currentAvatar,
      avatarUserSelected,
      avatarValid,
      askMeAboutMandatory,
      isValid
    });
    
    this.setState({ formValid: isValid });
    
    // Update context step validity if available
    const { onboardingContext } = this.props;
    if (onboardingContext && typeof onboardingContext.setStepValid === 'function') {
      onboardingContext.setStepValid(isValid);
    }
    
    return isValid;
  }
  
  async saveHeadlineAndBio() {
    // Check form validity first
    if (!this.checkFormValidity()) {
      this.setState({ error: this.getTranslatedText('error.form') });
      return false;
    }

    // Additional validation to ensure headline is user-entered and not empty
    const { headline, headlineUserEntered, currentAvatar, avatarUserSelected } = this.state;
    if (!headline || headline.trim() === '' || !headlineUserEntered) {
      this.setState({ error: this.getTranslatedText('headline.warning') });
      return false;
    }
    
    // Ensure avatar is user-selected
    if (!currentAvatar || !avatarUserSelected) {
      this.setState({ error: this.getTranslatedText('avatar.warning') });
      return false;
    }

    try {
      this.setState({ loading: true, error: null });
      
      const { headline, bio, askMeAbout, currentAvatar } = this.state;
      
      // Execute the GraphQL mutation
      const response = await this.props.mutate({
        variables: {
          headline,
          bio,
          ask_me_about: askMeAbout,
          profile_picture: currentAvatar,
          locale: this.props.currentLanguage
        }
      });
      
      const data = response.data;
      
      if (data?.updateCurrentUser) {
        
        // Update user in context if onUserUpdate is provided
        if (typeof this.props.onUserUpdate === 'function') {
          this.props.onUserUpdate({
            headline,
            bio,
            ask_me_about: askMeAbout,
            profile_picture: currentAvatar
          });
        }
        
        // Also update window.__WIZARD_DATA__ for persistence
        if (window.__WIZARD_DATA__ && window.__WIZARD_DATA__.resumeData) {
          window.__WIZARD_DATA__.resumeData = {
            ...window.__WIZARD_DATA__.resumeData,
            headline,
            bio,
            ask_me_about: askMeAbout,
            profile_picture: currentAvatar
          };
        }
        
        // Update context user if available
        const { onboardingContext } = this.props;
        if (onboardingContext && typeof onboardingContext.updateUser === 'function') {
          onboardingContext.updateUser({
            headline,
            bio,
            ask_me_about: askMeAbout,
            profile_picture: currentAvatar
          });
        }
        
        this.setState({ loading: false });
        return true;
      } else {
        throw new Error('Failed to update headline and bio');
      }
    } catch (err) {
      this.setState({ 
        error: this.getTranslatedText('error.save'),
        loading: false
      });
      return false;
    }
  }
  
  async handleContinue() {
    try {    
      const saveSuccessful = await this.saveHeadlineAndBio();
      
      if (saveSuccessful) {
        // Prioritize props.onNext (Redux/OnboardingWizard) which saves to server
        if (typeof this.props.onNext === 'function') {
          this.props.onNext();
          return true;
        }
        
        // Fallback to context navigation if props.onNext not available
        const { onboardingContext } = this.props;
        if (onboardingContext && typeof onboardingContext.goToNextStep === 'function') {
          onboardingContext.goToNextStep();
          return true;
        }
        
        // Last resort: direct URL navigation
        window.location.href = '/succession/dashboard';
        return true;
      }
      
      return false;
    } catch (err) {
      console.error("Error in continue button click handler:", err);
      this.setState({ error: this.getTranslatedText('error.navigate') });
      return false;
    }
  }
  
  handleBack() {
    
    // Prefer props navigation (from OnboardingWizard) over context navigation
    // This ensures proper synchronization when used inside OnboardingWizard
    if (typeof this.props.onBack === 'function') {
      this.props.onBack();
      return;
    }
    
    // Try using context navigation as fallback
    const { onboardingContext } = this.props;
    if (onboardingContext && typeof onboardingContext.goToPreviousStep === 'function') {
      onboardingContext.goToPreviousStep();
      return;
    }
    
    // Final fallback to direct URL navigation
    window.location.href = '/onboarding/practice_information';
  }
  
  // Get translated text based on current language
  getTranslatedText(key) {
    const { currentLanguage } = this.props;
    
    // Define translations
    const translations = {
      en: {
        'title': 'Your Professional Headline',
        'subtitle.student': 'Share a headline that showcases your education and career aspirations.',
        'subtitle.professional': 'Create a headline that showcases your expertise and captures attention.',
        'headline.section.title': 'Professional Headline',
        'headline.section.desc': 'Your headline appears prominently in your profile and helps others understand your focus.',
        'headline.warning': 'Please enter a professional headline to continue',
        'headline.ai.label': 'AI-Powered Headline',
        'headline.ai.generating': 'Generating headline with AI...',
        'headline.placeholder': 'Example: Financial Advisor specializing in retirement planning with 15 years of experience',
        // Growth bucket — user wants to acquire / merge / scale
        'headline.growth.one':   'Established {agent_word} in {user_city} seeking opportunities to grow.',
        'headline.growth.two':   'Open to acquisitions and partnerships in {user_city} — let\'s build something bigger.',
        'headline.growth.three': 'Ambitious {agent_word} in {user_city} ready to merge, acquire, or team up.',
        'headline.growth.four':  '{years_practicing} years of growth and counting in {user_city}. What\'s next together?',
        'headline.growth.five':  '{product_name} practice in {user_city} exploring ways to scale.',
        // Succession bucket — user wants to transition out / sell / find a successor
        'headline.succession.one':   'Thoughtfully planning the next chapter of my {user_city} practice.',
        'headline.succession.two':   'Experienced {agent_word} in {user_city} preparing a careful succession.',
        'headline.succession.three': '{years_practicing} years of client relationships ready for the right successor.',
        'headline.succession.four':  'Legacy-minded {agent_word} exploring succession in {user_city}.',
        'headline.succession.five':  '{product_name} practice with deep roots in {user_city} — open to succession conversations.',
        // Exploring bucket — no_motivation / default
        'headline.exploring.one':   '{agent_word} in {user_city} — curious about what\'s possible.',
        'headline.exploring.two':   'Experienced {agent_word} in {user_city} open to conversations with peers.',
        'headline.exploring.three': 'Building something meaningful in {user_city}. Happy to connect.',
        'headline.exploring.four':  '{years_practicing} years in {user_city} and keeping an eye on what\'s next.',
        'headline.exploring.five':  'Thoughtful {agent_word} in {user_city} — always open to new possibilities.',
        'headline.template.fallback': '{agent_word} serving {location}',
        'avatar.section.title': 'Choose an Avatar',
        'avatar.section.desc': 'You must select an avatar that will represent you on your profile.',
        'avatar.warning': 'Please select an avatar to continue',
        'avatar.loading': 'Loading available avatars...',
        'avatar.selected': 'Selected',
        'askme.section.title': 'Ask Me About',
        'askme.section.desc': 'List topics that potential clients or partners should ask you about.',
        'askme.required': 'This field is required',
        'askme.placeholder': 'Example: Retirement planning, estate planning, college funding strategies...',
        'error.form': 'Please fill out all required fields before continuing.',
        'error.save': 'There was a problem saving your information. Please try again.',
        'error.navigate': 'There was a problem navigating to the next step. Please try again.',
        'back': 'Back',
        'continue': 'Continue',
        'saving': 'Saving...'
      },
      fr: {
        'title': 'Votre Titre Professionnel',
        'subtitle.student': 'Partagez un titre qui met en valeur votre formation et vos aspirations professionnelles.',
        'subtitle.professional': 'Créez un titre qui met en évidence votre expertise et capte l\'attention.',
        'headline.section.title': 'Titre Professionnel',
        'headline.section.desc': 'Votre titre apparaît de manière bien visible dans votre profil et aide les autres à comprendre votre orientation.',
        'headline.warning': 'Veuillez entrer un titre professionnel pour continuer',
        'headline.ai.label': 'Titre Généré par IA',
        'headline.ai.generating': 'Génération du titre avec IA...',
        'headline.placeholder': 'Exemple: Conseiller financier spécialisé dans la planification de la retraite avec 15 ans d\'expérience',
        // Croissance
        'headline.growth.one':   '{agent_word} établi à {user_city} cherchant des opportunités de croissance.',
        'headline.growth.two':   'Ouvert aux acquisitions et partenariats à {user_city} — bâtissons quelque chose de plus grand.',
        'headline.growth.three': '{agent_word} ambitieux à {user_city} prêt à fusionner, acquérir ou collaborer.',
        'headline.growth.four':  '{years_practicing} ans de croissance à {user_city}. Et la suite?',
        'headline.growth.five':  'Cabinet {product_name} à {user_city} explorant de nouvelles avenues de croissance.',
        // Succession
        'headline.succession.one':   'Planification réfléchie du prochain chapitre de mon cabinet à {user_city}.',
        'headline.succession.two':   '{agent_word} expérimenté à {user_city} préparant une succession soignée.',
        'headline.succession.three': '{years_practicing} ans de relations clients prêtes pour le bon successeur.',
        'headline.succession.four':  '{agent_word} soucieux de son héritage, explorant la succession à {user_city}.',
        'headline.succession.five':  'Cabinet {product_name} bien enraciné à {user_city} — ouvert aux discussions de succession.',
        // Exploration
        'headline.exploring.one':   '{agent_word} à {user_city} — curieux de découvrir les possibilités.',
        'headline.exploring.two':   '{agent_word} expérimenté à {user_city}, ouvert aux échanges avec ses pairs.',
        'headline.exploring.three': 'Bâtir quelque chose d\'important à {user_city}. Heureux de se connecter.',
        'headline.exploring.four':  '{years_practicing} ans à {user_city}, toujours à l\'affût de la suite.',
        'headline.exploring.five':  '{agent_word} réfléchi à {user_city} — toujours ouvert aux nouvelles possibilités.',
        'headline.template.fallback': '{agent_word} desservant {location}',
        'avatar.section.title': 'Choisissez un Avatar',
        'avatar.section.desc': 'Vous devez sélectionner un avatar qui vous représentera sur votre profil.',
        'avatar.warning': 'Veuillez sélectionner un avatar pour continuer',
        'avatar.loading': 'Chargement des avatars disponibles...',
        'avatar.selected': 'Sélectionné',
        'askme.section.title': 'Interrogez-moi Sur',
        'askme.section.desc': 'Énumérez les sujets sur lesquels les clients ou partenaires potentiels devraient vous interroger.',
        'askme.required': 'Ce champ est obligatoire',
        'askme.placeholder': 'Exemple: Planification de la retraite, planification successorale, stratégies de financement des études...',
        'error.form': 'Veuillez remplir tous les champs obligatoires avant de continuer.',
        'error.save': 'Un problème est survenu lors de l\'enregistrement de vos informations. Veuillez réessayer.',
        'error.navigate': 'Un problème est survenu lors de la navigation vers l\'étape suivante. Veuillez réessayer.',
        'back': 'Retour',
        'continue': 'Continuer',
        'saving': 'Enregistrement...'
      },
      'en-GB': {
        // Only overrides: British phrasing for the headline variants. Anything
        // not listed here falls through to the `en` block via the lookup below.
        'headline.growth.one':   'Established {agent_word} in {user_city} seeking opportunities to grow the practice.',
        'headline.growth.two':   'Open to acquisitions and partnerships across {user_city} — keen to build something bigger.',
        'headline.growth.three': 'Ambitious {agent_word} in {user_city} keen to merge, acquire or team up.',
        'headline.growth.four':  '{years_practicing} years of steady growth in {user_city}. What shall we build next?',
        'headline.growth.five':  '{product_name} practice in {user_city} exploring how to scale.',
        'headline.succession.one':   'Carefully planning the next chapter for my {user_city} practice.',
        'headline.succession.two':   'Experienced {agent_word} in {user_city} preparing a considered succession.',
        'headline.succession.three': '{years_practicing} years of client relationships ready for the right successor.',
        'headline.succession.four':  'Legacy-minded {agent_word} exploring succession across {user_city}.',
        'headline.succession.five':  '{product_name} practice with deep roots in {user_city} — open to succession conversations.',
        'headline.exploring.one':   '{agent_word} in {user_city} — curious about what\'s next.',
        'headline.exploring.two':   'Experienced {agent_word} in {user_city}, happy to chat with likeminded Partners.',
        'headline.exploring.three': 'Building something worthwhile in {user_city}. Pleased to connect.',
        'headline.exploring.four':  '{years_practicing} years in {user_city} and keeping an eye on what\'s next.',
        'headline.exploring.five':  'Thoughtful {agent_word} in {user_city} — always open to a good conversation.',
      }
    };

    // Return translated text. For en-GB, try en-GB first, then fall back to en.
    return translations[currentLanguage]?.[key] || translations['en'][key] || key;
  }
  
  render() {
    const { 
      headline, 
      bio, 
      askMeAbout,
      currentAvatar,
      loading,
      error,
      formValid,
      avatarError,
      headlineError,
      bioError,
      aiGenerating
    } = this.state;
    
    const { mutateLoading = false, user = {}, availableAvatars = [] } = this.props;
    const companySettings = this.getCompanySettings();
    
    // Check for ai_headlines feature - handle both object format (from controller) and array format (from GraphQL)
    // Controller format: features = { ai_headlines: true, ... } (object with boolean values)
    // GraphQL format: features = ['ai_headlines', ...] (array of strings)
    const features = companySettings.features;
    const featureTypes = companySettings.feature_types || [];
    
    const hasAiHeadlines = 
      // Check object format (from controller)
      (features && typeof features === 'object' && !Array.isArray(features) && features.ai_headlines === true) ||
      // Check array format (from GraphQL)
      (Array.isArray(features) && features.includes('ai_headlines')) ||
      // Check feature_types array (always an array of strings)
      (Array.isArray(featureTypes) && featureTypes.includes('ai_headlines'));
    
    // Check if "Ask me about" should be shown
    const showAskMeAbout = companySettings.ask_me_about_question || false;
    const askMeAboutMandatory = companySettings.ask_me_about_mandatory || false;
    
    const isStudent = user?.is_student || false;
    
    return (
      <div className="bg-succession-bg-primary shadow-succession-lg rounded-xl p-8" style={{ backgroundColor: 'var(--succession-bg-primary, #ffffff)' }}>
        <h2 className="text-2xl font-medium text-succession-text-primary mb-3 text-center">{this.getTranslatedText('title')}</h2>
        <p className="text-md text-succession-text-secondary mb-8 text-center max-w-2xl mx-auto">
          {isStudent 
            ? this.getTranslatedText('subtitle.student')
            : this.getTranslatedText('subtitle.professional')}
        </p>
        
        {error && (
          <div className="mb-6 p-4 bg-succession-error-bg border border-red-200 rounded-lg shadow-sm">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-succession-error mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zm-1 9a1 1 0 01-1-1v-4a1 1 0 112 0v4a1 1 0 01-1 1z" clipRule="evenodd" />
              </svg>
              <p className="text-sm text-succession-error">{error}</p>
            </div>
          </div>
        )}
        
        <div className="grid grid-cols-1 gap-8 mb-8">
          {/* Headline Section */}
          <div className="space-y-4 border border-succession-border rounded-lg p-6 shadow-succession-sm bg-succession-bg-primary hover:shadow-succession-md transition-shadow duration-300" style={{ backgroundColor: 'var(--succession-bg-primary, #ffffff)' }}>
            <h3 className="text-lg font-medium text-succession-text-primary border-b border-succession-border pb-3">
              {this.getTranslatedText('headline.section.title')} <span className="text-red-500">*</span>
            </h3>
            <p className="text-sm text-succession-text-secondary">{this.getTranslatedText('headline.section.desc')}</p>
            
            {(!headline || headline.trim() === '' || !this.state.headlineUserEntered) && (
              <p className="text-xs text-amber-600 bg-amber-50 p-2.5 rounded-md flex items-center">
                <svg className="w-4 h-4 mr-1.5 text-amber-500" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {this.getTranslatedText('headline.warning')}
              </p>
            )}
            
            <div className="mt-4">
              {hasAiHeadlines ? (
                <div className="m-0">
                  <div className="flex items-center mb-1">
                    {this.state.aiGenerating ? (
                      <svg className="animate-spin h-5 w-5 text-succession-primary mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" style={{ color: 'var(--succession-primary, #5489B6) !important' }}>
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : (
                      <svg className="h-5 w-5 text-succession-primary mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color: 'var(--succession-primary, #5489B6) !important' }}>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    )}
                    <span className="font-medium text-succession-primary" style={{ color: 'var(--succession-primary, #5489B6) !important' }}>
                      {this.state.aiGenerating 
                        ? this.getTranslatedText('headline.ai.generating')
                        : this.getTranslatedText('headline.ai.label')
                      }
                    </span>
                  </div>
                  <div className="relative">
                    <style>
                      {`
                        .ai-headline-wrapper {
                          margin: 0 !important;
                          padding: 0 0 50px 0 !important;
                          background: transparent !important;
                        }
                        .ai-headline-wrapper > div,
                        .ai-headline-wrapper div {
                          margin: 0 !important;
                          padding: 0 !important;
                          background: transparent !important;
                          width: 100% !important;
                          max-width: 100% !important;
                        }
                        .ai-headline-wrapper textarea {
                          margin: 0 !important;
                          padding: 12px !important;
                          width: 100% !important;
                          box-sizing: border-box !important;
                          border: 1px solid #d1d5db !important;
                          border-radius: 8px !important;
                          background: #f3f4f6 !important;
                        }
                        .ai-headline-wrapper button {
                          position: absolute !important;
                          top: 100% !important;
                          right: 0 !important;
                          margin: 16px 0 0 0 !important;
                          display: inline-flex !important;
                          align-items: center !important;
                          padding: 8px 20px !important;
                          font-size: 14px !important;
                          white-space: nowrap !important;
                          background: #4f46e5 !important;
                          color: white !important;
                          border: none !important;
                          border-radius: 4px !important;
                          min-width: 180px !important;
                          width: auto !important;
                          text-align: center !important;
                          line-height: 1.2 !important;
                          overflow: hidden !important;
                          gap: 6px !important;
                        }
                        .ai-headline-wrapper button:disabled {
                          background: #9ca3af !important;
                          cursor: not-allowed !important;
                        }
                        .ai-headline-wrapper::after {
                          content: "";
                          display: table;
                          clear: both;
                        }
                      `}
                    </style>
                    <div className="ai-headline-wrapper">
                      <AIHeadline
                        headline={headline || ''}
                        updateHeadline={(newHeadline) => {
                          this.setState({ 
                            headline: newHeadline,
                            headlineUserEntered: true  // Mark as user-entered when AI headline is used
                          }, () => {
                            this.checkFormValidity();
                          });
                          // If onUserUpdate is available, update the user data
                          if (typeof this.props.onUserUpdate === 'function') {
                            this.props.onUserUpdate({ headline: newHeadline });
                          }
                        }}
                        stylesheetName={companySettings?.stylesheet_name || ''}
                        onGenerationStart={this.handleAiGenerationStart}
                        onGenerationEnd={this.handleAiGenerationEnd}
                        isGenerating={this.state.aiGenerating}
                      />
                    </div>
                  </div>
                  
                  {/* AI Privacy & Security Disclaimer */}
                  <div className="mt-4 p-3 bg-gray-100 border-l-4 border-orange-500 rounded">
                    <p className="font-bold text-sm mb-2 text-gray-800">
                      AI Privacy & Security
                    </p>
                    <p className="text-sm leading-relaxed text-gray-700">
                      Our AI features are designed with privacy, security, and fairness in mind. Client data is never used to train external AI models, and all use of AI complies with applicable privacy, confidentiality, and information security requirements. Please always review the details for accuracy and suitability as outputs may contain mistakes.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="relative">
                  <textarea
                    id="headline"
                    rows="3"
                    className="w-full p-3 border border-succession-border rounded-lg focus:ring-succession-primary focus:border-succession-primary shadow-sm transition duration-150 ease-in-out m-0 bg-succession-bg-primary text-succession-text-primary"
                    style={{ 
                      backgroundColor: 'var(--succession-bg-primary, #ffffff)',
                      borderColor: 'var(--succession-border, #e5e7eb)',
                      '--tw-ring-color': 'var(--succession-primary, #5489B6)',
                    }}
                    value={headline}
                    onChange={(e) => this.handleTextChange('headline', e.target.value)}
                    onFocus={() => {
                      // Mark as user-entered when they focus on the field (even if auto-generated)
                      if (!this.state.headlineUserEntered) {
                        this.setState({ headlineUserEntered: true }, () => {
                          this.checkFormValidity();
                        });
                      }
                    }}
                    placeholder={this.getTranslatedText('headline.placeholder')}
                  />
                  {headlineError && (
                    <p className="mt-1.5 text-sm text-succession-error flex items-center">
                      <svg className="w-3.5 h-3.5 mr-1.5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zm-1 9a1 1 0 01-1-1v-4a1 1 0 112 0v4a1 1 0 01-1 1z" clipRule="evenodd" />
                      </svg>
                      {headlineError}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
          
          {/* Avatar Selection Section */}
          <div className="space-y-4 border border-succession-border rounded-lg p-6 shadow-succession-sm bg-succession-bg-primary hover:shadow-succession-md transition-shadow duration-300" style={{ backgroundColor: 'var(--succession-bg-primary, #ffffff)' }}>
            <h3 className="text-lg font-medium text-succession-text-primary border-b border-succession-border pb-3">
              {this.getTranslatedText('avatar.section.title')} <span className="text-red-500">*</span>
            </h3>
            <p className="text-sm text-succession-text-secondary">{this.getTranslatedText('avatar.section.desc')}</p>
            
            {!this.state.avatarUserSelected && (
              <p className="text-xs text-amber-600 bg-amber-50 p-2.5 rounded-md flex items-center">
                <svg className="w-4 h-4 mr-1.5 text-amber-500" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {this.getTranslatedText('avatar.warning')}
              </p>
            )}
            
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {availableAvatars.length === 0 ? (
                <div className="flex items-center justify-center col-span-full py-8">
                  <svg className="animate-spin h-8 w-8 text-succession-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span className="ml-3 text-sm text-succession-text-muted">{this.getTranslatedText('avatar.loading')}</span>
                </div>
              ) : (
                availableAvatars.map(avatar => {
                  // Detect new avatar system from avatar naming pattern (avatar_XXX format)
                  const isNewStyleAvatar = avatar.match(/^avatar_\d{3}$/);
                  
                  // New avatars use .png, legacy avatars use -rectangle.png
                  const imageName = isNewStyleAvatar 
                    ? `${avatar}.png` 
                    : `${avatar}-rectangle.png`;
                  const isSelected = currentAvatar === imageName && this.state.avatarUserSelected;
                  
                  // Build company settings for getAvatarStyle
                  const avatarCompanySettings = {
                    avatar_directory: companySettings.avatar_directory,
                    avatar_background_color: companySettings.avatar_background_color,
                    primary_color: companySettings.marketplace_colors?.primary_color,
                  };
                  
                  // Get avatar style (URL and background color) - automatically detects new vs legacy avatars
                  const avatarStyle = getAvatarStyle(imageName, avatarCompanySettings);
                  
                  return (
                    <div key={avatar} className="flex flex-col items-center justify-center">
                      <button
                        type="button"
                        aria-label={`Select avatar ${avatar}`}
                        className={`
                          relative overflow-hidden rounded-lg transition-all duration-300
                          ${isSelected ? 
                            'ring-4 ring-succession-primary shadow-lg transform scale-105' : 
                            'ring-1 ring-succession-border hover:ring-succession-primary hover:shadow-md hover:scale-105'
                          }
                        `}
                        style={{
                          '--tw-ring-color': isSelected 
                            ? 'var(--succession-primary, #5489B6)' 
                            : 'var(--succession-border, #e5e7eb)',
                          backgroundColor: avatarStyle.backgroundColor,
                        }}
                        onClick={() => this.handleAvatarChange(imageName)}
                      >
                        <div className="w-16 h-16 sm:w-20 sm:h-20">
                          <img 
                            src={avatarStyle.url} 
                            alt={`Avatar ${avatar}`} 
                            className="w-full h-full object-cover"
                            style={avatarStyle.isNewStyle ? { objectFit: 'contain', objectPosition: 'bottom center', transform: 'scale(0.85)', transformOrigin: 'bottom center' } : {}}
                          />
                          {isSelected && (
                            <div className="absolute top-1 right-1">
                              <div className="bg-white rounded-full p-1 shadow-lg border-2 border-succession-primary">
                                <svg className="w-3 h-3 text-succession-primary" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              </div>
                            </div>
                          )}
                        </div>
                      </button>
                      {isSelected && (
                        <span className="mt-2 text-xs font-medium text-succession-primary">{this.getTranslatedText('avatar.selected')}</span>
                      )}
                    </div>
                  );
                })
              )}
            </div>
            
            {avatarError && (
              <p className="mt-3 text-sm text-succession-error flex items-center">
                <svg className="w-3.5 h-3.5 mr-1.5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zm-1 9a1 1 0 01-1-1v-4a1 1 0 112 0v4a1 1 0 01-1 1z" clipRule="evenodd" />
                </svg>
                {avatarError}
              </p>
            )}
          </div>
          
          {/* Ask Me About Section (conditional) */}
          {showAskMeAbout && (
            <div className="space-y-4 border border-succession-border rounded-lg p-6 shadow-succession-sm bg-succession-bg-primary hover:shadow-succession-md transition-shadow duration-300" style={{ backgroundColor: 'var(--succession-bg-primary, #ffffff)' }}>
              <h3 className="text-lg font-medium text-succession-text-primary border-b border-succession-border pb-3">
                {this.getTranslatedText('askme.section.title')}
                {askMeAboutMandatory && <span className="text-red-500 ml-1">*</span>}
              </h3>
              <p className="text-sm text-succession-text-secondary">
                {this.getTranslatedText('askme.section.desc')}
              </p>
              
              {askMeAboutMandatory && askMeAbout === '' && (
                <p className="text-xs text-amber-600 bg-amber-50 p-2.5 rounded-md flex items-center">
                  <svg className="w-4 h-4 mr-1.5 text-amber-500" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  {this.getTranslatedText('askme.required')}
                </p>
              )}
              
              <div className="mt-4">
                <textarea
                  id="askMeAbout"
                  rows="3"
                  className="w-full p-3 border border-succession-border rounded-lg focus:ring-succession-primary focus:border-succession-primary shadow-sm transition duration-150 ease-in-out bg-succession-bg-primary text-succession-text-primary"
                  style={{ 
                    backgroundColor: 'var(--succession-bg-primary, #ffffff)',
                    borderColor: 'var(--succession-border, #e5e7eb)',
                    '--tw-ring-color': 'var(--succession-primary, #5489B6)',
                  }}
                  value={askMeAbout}
                  onChange={(e) => this.handleTextChange('askMeAbout', e.target.value)}
                  placeholder={this.getTranslatedText('askme.placeholder')}
                />
              </div>
            </div>
          )}
        </div>

        {/* Navigation buttons */}
        <div className="flex justify-between items-center mt-8 pt-6 border-t border-succession-border">
          <button
            type="button"
            onClick={this.handleBack}
            className="inline-flex justify-center items-center py-2.5 px-6 border border-succession-border shadow-sm text-sm font-medium rounded-lg text-succession-text-secondary bg-succession-bg-primary hover:bg-succession-bg-secondary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-succession-primary transition-all duration-200"
            style={{ 
              backgroundColor: 'var(--succession-bg-primary, #ffffff)',
              borderColor: 'var(--succession-border, #e5e7eb)',
              '--tw-ring-color': 'var(--succession-primary, #5489B6)',
            }}
          >
            <svg className="mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            {this.getTranslatedText('back')}
          </button>
          
          <button 
            onClick={this.handleContinue}
            className={`inline-flex items-center py-2.5 px-6 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white transition-all duration-200 ${
              loading || mutateLoading || !formValid 
                ? 'bg-succession-primary opacity-60 cursor-not-allowed' 
                : 'bg-succession-primary hover:bg-succession-primary-hover hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-succession-primary'
            }`}
            style={{ 
              backgroundColor: 'var(--succession-primary, #5489B6)',
              '--tw-ring-color': 'var(--succession-primary, #5489B6)',
            }}
            disabled={loading || mutateLoading || !formValid}
          >
            {loading || mutateLoading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {this.getTranslatedText('saving')}
              </>
            ) : (
              <>
                {this.getTranslatedText('continue')}
                <svg className="ml-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </>
            )}
          </button>
        </div>
      </div>
    );
  }
}

// Connect component to both GraphQL and OnboardingContext
export default withOnboardingContext(
  graphql(UPDATE_CURRENT_USER, {
    options: { 
      errorPolicy: 'all' 
    }
  })(HeadlineAndAboutMeStep)
);

HeadlineAndAboutMeStep.propTypes = {
  user: PropTypes.object,
  companySettings: PropTypes.object,
  onNext: PropTypes.func,
  onUserUpdate: PropTypes.func,
  mutate: PropTypes.func,
  loading: PropTypes.bool,
  onBack: PropTypes.func,
  availableAvatars: PropTypes.array,
  currentLanguage: PropTypes.string,
  onboardingContext: PropTypes.object
}; 